import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Paper,
    Pagination
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import BlockIcon from "@mui/icons-material/Block";
import { useHistory } from "react-router-dom";
import { functions, firestore, auth, firebaseProjectId } from "../services/firebase";
import { StyledCard } from "../components/OSMDataAdmin/styled";
import { normalizeWhitespace } from "../lib/address";
import { useStateRegions, useStateRegionActions } from "../actions/stateRegions";
import { captureError } from "../lib/errorReporting";

// Base URL for the statewide HTTP functions. Uses the local Functions emulator
// in dev (REACT_APP_USE_EMULATOR=true) to keep dev isolated from the deployed
// prod functions; otherwise the deployed endpoints.
const statewideFunctionsBase = () =>
    process.env.REACT_APP_USE_EMULATOR === "true"
        ? `http://localhost:5001/${firebaseProjectId}/us-central1`
        : `https://us-central1-${firebaseProjectId}.cloudfunctions.net`;

const STATUS_LABELS = {
    query: "Running statewide query..."
};

const parseDeleteEmailAllowList = () => {
    const raw = process.env.REACT_APP_CAN_DELETE_EMAILS;
    if (!raw) {
        return new Set();
    }

    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return new Set(
                parsed
                    .map((email) => String(email || "").trim().toLowerCase())
                    .filter(Boolean)
            );
        }
    } catch (_error) {
        // fall through to string parsing
    }

    return new Set(
        raw
            .split(",")
            .map((value) => value.replace(/[[\]\s"']/g, ""))
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean)
    );
};

const CAN_DELETE_EMAILS = parseDeleteEmailAllowList();

const getCollectionNames = (stateKey) => ({
    queries: `statewide_queries_${stateKey}`,
    precommit: `statewide_precommit_${stateKey}`,
    master: `statewide_${stateKey}`
});

export function StatewideSearch() {
    const history = useHistory();

    const stateRegions = useStateRegions();
    const { fetchStateRegions } = useStateRegionActions();

    const [selectedStateId, setSelectedStateId] = useState("");
    const [filterStateId, setFilterStateId] = useState("");
    const [queries, setQueries] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [pipelineStage, setPipelineStage] = useState(null);
    const [pipelineRunning, setPipelineRunning] = useState(false);
    const [report, setReport] = useState(null);
    const [zipProgress, setZipProgress] = useState({ processed: 0, total: 0, lastZip: "", pointIndex: 0, pointTotal: 0 });
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [pruning, setPruning] = useState(false);
    const [page, setPage] = useState(1);
    const PAGE_SIZE_OPTIONS = [25, 50, 100];
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [sortDirection, setSortDirection] = useState("desc");
    const [sortField, setSortField] = useState("created");
    const [excludingIds, setExcludingIds] = useState(new Set());

    const addToExcludedCallable = useMemo(
        () => functions.httpsCallable("addOSMBarsToBlacklist"),
        []
    );

    const toNumber = (value) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
    };

    const extractCoords = (locationValue) => {
        if (!locationValue) return { lat: null, lng: null };

        if (Array.isArray(locationValue?.coordinates) && locationValue.coordinates.length >= 2) {
            return {
                lng: toNumber(locationValue.coordinates[0]),
                lat: toNumber(locationValue.coordinates[1])
            };
        }

        if (Array.isArray(locationValue) && locationValue.length >= 2) {
            return {
                lat: toNumber(locationValue[0]),
                lng: toNumber(locationValue[1])
            };
        }

        return {
            lat: toNumber(locationValue?.lat ?? locationValue?.latitude),
            lng: toNumber(locationValue?.lng ?? locationValue?.lon ?? locationValue?.longitude)
        };
    };

    const currentUserEmail = (auth?.currentUser?.email || "").toLowerCase();
    const canRunStatewide = useMemo(() => CAN_DELETE_EMAILS.has(currentUserEmail), [currentUserEmail]);

    const selectedState = useMemo(
        () => stateRegions.find((state) => state.id === selectedStateId),
        [stateRegions, selectedStateId]
    );
    const stateAbbr = (selectedState?.abbr || selectedState?.id || "").toUpperCase();
    const selectedFilterState = useMemo(
        () => stateRegions.find((state) => state.id === filterStateId),
        [stateRegions, filterStateId]
    );
    const filterStateAbbr = (selectedFilterState?.abbr || selectedFilterState?.id || "").toUpperCase();
    const stateKey = filterStateAbbr ? filterStateAbbr.toLowerCase() : "";

    useEffect(() => {
        fetchStateRegions();
    }, [fetchStateRegions]);

    useEffect(() => {
        if (!stateKey) {
            setQueries([]);
            setRows([]);
            setLoading(false);
            return undefined;
        }

        const { queries: queriesName, master: masterName } = getCollectionNames(stateKey);

        const queriesUnsub = firestore
            .collection(queriesName)
            .orderBy("query_date", "desc")
            .onSnapshot((snapshot) => {
                const rows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                setQueries(rows);
            }, (err) => {
                captureError(err, { view: "StatewideSearch", action: "queries.onSnapshot" });
                console.error("StatewideSearch queries snapshot error", err);
            });

        setLoading(true);
        const masterUnsub = firestore
            .collection(masterName)
            .orderBy("created_at", "desc")
            .limit(500)
            .onSnapshot(
                (snapshot) => {
                    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                    setRows(data);
                    setLoading(false);
                },
                (err) => {
                    setError(err?.message || "Failed to load statewide list");
                    setLoading(false);
                }
            );

        return () => {
            queriesUnsub();
            masterUnsub();
        };
    }, [stateKey]);


    useEffect(() => {
        setError("");
        setSuccess("");
        setReport(null);
    }, [stateKey]);

    const rowsWithParts = useMemo(() => {
        return rows.map((row) => {
            return {
                ...row,
                addressParts: {
                    street: normalizeWhitespace(row.address_1 || ""),
                    city: normalizeWhitespace(row.city || ""),
                    state: normalizeWhitespace(row.state || ""),
                    postalCode: normalizeWhitespace(row.zip || "")
                }
            };
        });
    }, [rows]);

    const sortedRows = useMemo(() => {
        const copy = [...rowsWithParts];
        copy.sort((a, b) => {
            const direction = sortDirection === "desc" ? -1 : 1;
            if (sortField === "city") {
                return direction * (a.addressParts.city || "").localeCompare(b.addressParts.city || "", undefined, { sensitivity: "base" });
            }
            if (sortField === "zip") {
                return direction * (a.addressParts.postalCode || "").localeCompare(b.addressParts.postalCode || "");
            }
            if (sortField === "street") {
                return direction * (a.addressParts.street || "").localeCompare(b.addressParts.street || "", undefined, { sensitivity: "base" });
            }
            if (sortField === "name") {
                return direction * (a.bar_name || "").localeCompare(b.bar_name || "", undefined, { sensitivity: "base" });
            }
            const aDate = a?.created_at?.toMillis
                ? a.created_at.toMillis()
                : a?.created_at
                    ? new Date(a.created_at).getTime()
                    : 0;
            const bDate = b?.created_at?.toMillis
                ? b.created_at.toMillis()
                : b?.created_at
                    ? new Date(b.created_at).getTime()
                    : 0;
            return sortDirection === "desc" ? bDate - aDate : aDate - bDate;
        });
        return copy;
    }, [rowsWithParts, sortDirection, sortField]);

    const filtered = useMemo(() => {
        const f = filter.trim().toLowerCase();
        if (!f) return sortedRows;
        return sortedRows.filter((row) => {
            const name = (row.bar_name || "").toLowerCase();
            const addr = (row.formatted_address || "").toLowerCase();
            const city = (row.addressParts?.city || "").toLowerCase();
            const zip = (row.addressParts?.postalCode || "").toLowerCase();
            return name.includes(f) || addr.includes(f) || city.includes(f) || zip.includes(f);
        });
    }, [filter, sortedRows]);

    const pageCount = useMemo(() => Math.max(1, Math.ceil(filtered.length / pageSize)), [filtered.length, pageSize]);

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    const handlePageSizeChange = (event) => {
        const next = Number(event.target.value) || PAGE_SIZE_OPTIONS[0];
        setPageSize(next);
        setPage(1);
    };

    const handleSortChange = (field) => {
        setSortField((current) => {
            if (current === field) {
                setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
                return current;
            }
            setSortDirection("asc");
            return field;
        });
    };

    const renderSortLabel = (label, field) => (
        <TableSortLabel
            active={sortField === field}
            direction={sortDirection}
            onClick={() => handleSortChange(field)}
            hideSortIcon={false}
            IconComponent={(props) => (
                <ExpandMoreRoundedIcon
                    {...props}
                    fontSize="small"
                    sx={{
                        ml: 0.25,
                        transition: "transform 120ms ease",
                        transform: sortDirection === "desc" ? "rotate(0deg)" : "rotate(180deg)"
                    }}
                />
            )}
        >
            {label}
        </TableSortLabel>
    );

    const runPrune = async (silent = false) => {
        if (!filterStateAbbr) {
            return;
        }
        if (!silent) {
            setSuccess("");
            setError("");
        }
        try {
            setPruning(true);
            const endpoint = `${statewideFunctionsBase()}/pruneStatewideMaster`;
            const res = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ stateAbbr: filterStateAbbr })
            });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const data = await res.json();
            const { removed, checked } = data || {};
            setSuccess(`Refreshed. Checked ${checked || 0}, removed ${removed || 0} that now exist in Supabase.`);
        } catch (err) {
            captureError(err, { view: "StatewideSearch", action: "refreshStatewideList" });
            setError(err?.message || "Failed to refresh statewide list");
        } finally {
            setPruning(false);
        }
    };

    useEffect(() => {
        if (!filterStateAbbr) {
            return;
        }
        runPrune(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStateAbbr]);

    const callStatewideEndpoint = async (endpoint, payload) => {
        const res = await fetch(`${statewideFunctionsBase()}/${endpoint}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload || {})
        });
        if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
    };

    const runTestQuery = async () => {
        if (!stateAbbr) {
            throw new Error("Select a state first.");
        }

        const response = await callStatewideEndpoint("statewideSearchTest", { stateAbbr });
        const { insertedCount } = response || {};
        return { insertedCount };
    };

    const runZipStep = async () => {
        const response = await callStatewideEndpoint("statewideSearchQuery", { stateAbbr });
        return response || {};
    };

    const runPipeline = async (mode) => {
        if (!canRunStatewide) {
            setError("You do not have access to run statewide searches.");
            return;
        }

        const latestQuery = queries[0];
        if (latestQuery?.status === "completed") {
            setError("Statewide search has already been run for this state.");
            return;
        }

        setError("");
        setSuccess("");
        setReport(null);
        setPipelineRunning(true);
        try {
            setPipelineStage("query");
            if (mode === "test") {
                const queryResult = await runTestQuery();
                setReport({
                    processedZips: queryResult?.processedZips || 1,
                    totalZips: queryResult?.totalZips || 0,
                    insertedTotal: queryResult?.insertedCount || 0,
                    skippedBlacklisted: 0,
                    skippedMissing: 0,
                    lastZip: queryResult?.zip || ""
                });
                setSuccess(`Test query completed for ZIP ${queryResult?.zip || ""}.`);
                return;
            }

            let latest = null;
            while (!latest || latest?.status !== "completed") {
                // eslint-disable-next-line no-await-in-loop
                latest = await runZipStep();
                const processed = Number(latest?.processedZips || 0);
                const total = Number(latest?.totalZips || 0);
                setZipProgress({
                    processed,
                    total,
                    lastZip: latest?.lastZip || "",
                    pointIndex: latest?.zipPointIndex || 0,
                    pointTotal: latest?.zipPointTotal || 0
                });
                setReport({
                    processedZips: processed,
                    totalZips: total,
                    insertedTotal: latest?.insertedTotal || 0,
                    skippedBlacklisted: latest?.skippedBlacklisted || 0,
                    skippedMissing: latest?.skippedMissing || 0,
                    lastZip: latest?.lastZip || "",
                    zipPointIndex: latest?.zipPointIndex || 0,
                    zipPointTotal: latest?.zipPointTotal || 0
                });
            }

            setSuccess(`Statewide search completed. Processed ${latest?.totalZips || 0} ZIP codes.`);
        } catch (err) {
            captureError(err, { view: "StatewideSearch", action: "runPipeline" });
            setError(err?.message || "Pipeline failed");
        } finally {
            setPipelineStage(null);
            setPipelineRunning(false);
        }
    };

    const hasSelectedState = Boolean(stateKey);
    const latestQuery = queries[0];
    const hasCompletedState = latestQuery?.status === "completed";

    const handleExclude = async (row) => {
        if (!row?.id || !stateKey) return;
        setError("");
        setSuccess("");
        setExcludingIds((prev) => new Set(prev).add(row.id));
        try {
            await addToExcludedCallable({
                bars: [
                    {
                        bar_name: row.bar_name,
                        bar_address: row.formatted_address
                    }
                ]
            });
            await firestore.collection(`statewide_${stateKey}`).doc(row.id).delete();
            setRows((prev) => prev.filter((item) => item.id !== row.id));
            setSuccess("Excluded venue and removed it from the statewide list.");
        } catch (err) {
            captureError(err, { view: "StatewideSearch", action: "excludeVenue" });
            setError(err?.message || "Failed to exclude venue");
        } finally {
            setExcludingIds((prev) => {
                const next = new Set(prev);
                next.delete(row.id);
                return next;
            });
        }
    };

    const handleAdd = (row) => {
        const name = row?.bar_name || "";
        const address = row?.formatted_address || "";
        const { lat, lng } = extractCoords(row?.location);
        const params = new URLSearchParams();
        if (name) params.set("name", name);
        if (address) params.set("address", address);
        if (lat !== null && lng !== null) {
            params.set("lat", String(lat));
            params.set("lng", String(lng));
        }
        const search = params.toString();

        history.push({
            pathname: "/bar/new",
            search: search ? `?${search}` : "",
            state: {
                prefillBarName: name,
                prefillBarAddress: address,
                prefillLatitude: lat,
                prefillLongitude: lng,
                prefillDataSource: "statewide"
            }
        });
    };

    const pagedRows = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    return (
        <PageContainer>
            <HeaderRow>
                <Button size="small" variant="outlined" onClick={() => runPrune(false)} disabled={pruning || !canRunStatewide || !stateAbbr}>
                    {pruning ? "Refreshing..." : "Refresh"}
                </Button>
            </HeaderRow>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                Run a single statewide search by iterating every ZIP code for a selected state.
            </Typography>

            {!canRunStatewide && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You do not have permission to run statewide searches.
                </Alert>
            )}

            {hasCompletedState ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Statewide search has already been run for this state.
                </Alert>
            ) : null}

            {error ? <Alert severity="error" sx={{ marginBottom: 2 }}>{error}</Alert> : null}
            {success ? <Alert severity="success" sx={{ marginBottom: 2 }}>{success}</Alert> : null}

            <StyledCard>
                <Section>
                    <Typography variant="h6" gutterBottom>Run Statewide Search</Typography>
                    <RunControls>
                        <FormControl size="small" variant="outlined" className="state-control">
                            <InputLabel id="statewide-run-state">Run State</InputLabel>
                            <Select
                                labelId="statewide-run-state"
                                label="Run State"
                                value={selectedStateId}
                                onChange={(event) => setSelectedStateId(event.target.value || "")}
                                fullWidth
                            >
                                <MenuItem value="">Select state</MenuItem>
                                {stateRegions
                                    .filter((state) => state.name && typeof state.name === "string")
                                    .map((state) => (
                                        <MenuItem key={state.id} value={state.id}>
                                            {state.name}{state.abbr ? ` (${state.abbr})` : ""}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            color="primary"
                                onClick={() => runPipeline("full")}
                                disabled
                        >
                                {pipelineRunning ? "Running..." : "Run"}
                        </Button>
                    </RunControls>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        This can only be run once per state. The pipeline will query each ZIP code and add new bars to the state list as it runs.
                    </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            Please contact your admin to run this functionality.
                        </Typography>
                </Section>
            </StyledCard>

            {pipelineRunning || pipelineStage ? (
                <StyledCard>
                    <Section>
                        <Typography variant="body1" gutterBottom>
                            {STATUS_LABELS[pipelineStage] || "Running statewide pipeline..."}
                            {zipProgress.total
                                ? ` ZIP ${zipProgress.processed} of ${zipProgress.total}`
                                : ""}
                            {zipProgress.pointTotal
                                ? ` (point ${zipProgress.pointIndex} of ${zipProgress.pointTotal})`
                                : ""}
                        </Typography>
                        <CircularProgress size={24} />
                    </Section>
                </StyledCard>
            ) : null}

            {report ? (
                <StyledCard>
                    <Section>
                        <Typography variant="h6" gutterBottom>Run summary</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Processed {report.processedZips || 0} of {report.totalZips || 0} ZIP codes.
                            {report.zipPointTotal
                                ? ` Point ${report.zipPointIndex || 0} of ${report.zipPointTotal}.`
                                : ""}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Added {report.insertedTotal || 0} bars so far. Skipped {report.skippedMissing || 0} missing data and {report.skippedBlacklisted || 0} blacklisted.
                        </Typography>
                        {report.lastZip ? (
                            <Typography variant="body2" color="textSecondary">
                                Last ZIP processed: {report.lastZip}
                            </Typography>
                        ) : null}
                    </Section>
                </StyledCard>
            ) : null}

            <StateFilter>
                <FormControl size="small" variant="outlined" className="state-control">
                    <InputLabel id="statewide-filter-state">Filter State</InputLabel>
                    <Select
                        labelId="statewide-filter-state"
                        label="Filter State"
                        value={filterStateId}
                        onChange={(event) => setFilterStateId(event.target.value || "")}
                        fullWidth
                    >
                        <MenuItem value="">Select state</MenuItem>
                        {stateRegions
                            .filter((state) => state.name && typeof state.name === "string")
                            .map((state) => (
                                <MenuItem key={state.id} value={state.id}>
                                    {state.name}{state.abbr ? ` (${state.abbr})` : ""}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </StateFilter>

            <Filters>
                <TextField
                    label="Filter by name or address"
                    variant="outlined"
                    size="small"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    fullWidth
                    disabled={!hasSelectedState}
                />
                <Typography variant="body2" color="textSecondary">
                    Showing {filtered.length} of {rows.length}
                </Typography>
            </Filters>

            <Paper sx={{ padding: 2 }}>
                {loading ? (
                    <LoadingWrap>
                        <CircularProgress size={28} />
                    </LoadingWrap>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {renderSortLabel("Name", "name")}
                                </TableCell>
                                <TableCell>
                                    {renderSortLabel("Street", "street")}
                                </TableCell>
                                <TableCell>
                                    {renderSortLabel("City", "city")}
                                </TableCell>
                                <TableCell>State</TableCell>
                                <TableCell>
                                    {renderSortLabel("Zip", "zip")}
                                </TableCell>
                                <TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
                                    {renderSortLabel("Created At", "created")}
                                </TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pagedRows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.bar_name}</TableCell>
                                    <TableCell>{row.addressParts?.street || ""}</TableCell>
                                    <TableCell>{row.addressParts?.city || ""}</TableCell>
                                    <TableCell>{row.addressParts?.state || ""}</TableCell>
                                    <TableCell>{row.addressParts?.postalCode || ""}</TableCell>
                                    <TableCell>{formatCreatedAt(row.created_at)}</TableCell>
                                    <TableCell align="right">
                                        <Box display="flex" justifyContent="flex-end" gap={1}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => handleAdd(row)}
                                            >
                                                Add
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<BlockIcon />}
                                                onClick={() => handleExclude(row)}
                                                disabled={excludingIds.has(row.id) || !canRunStatewide}
                                            >
                                                {excludingIds.has(row.id) ? "Removing..." : "Exclude"}
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {pagedRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography variant="body2" color="textSecondary">No results</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                )}
                <PaginationRow>
                    <div className="rows-per-page">
                        <span className="label">Rows per page</span>
                        <Select size="small" value={pageSize} onChange={handlePageSizeChange}>
                            {PAGE_SIZE_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </Select>
                    </div>
                    <div className="page-controls">
                        <Typography variant="body2" className="page-summary">
                            {filtered.length === 0
                                ? "No results"
                                : `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, filtered.length)} of ${filtered.length}`}
                        </Typography>
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={(_, val) => setPage(val)}
                            color="primary"
                            size="small"
                            shape="rounded"
                            showFirstButton
                            showLastButton
                            siblingCount={1}
                            boundaryCount={1}
                        />
                    </div>
                </PaginationRow>
            </Paper>
        </PageContainer>
    );
}

function formatCreatedAt(value) {
    if (!value) return "";
    const millis = value?.toMillis ? value.toMillis() : new Date(value).getTime();
    if (!millis || Number.isNaN(millis)) return "";
    return new Date(millis).toLocaleDateString();
}

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
`;

const Filters = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const StateFilter = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 4px;

    .state-control {
        min-width: 260px;
    }
`;

const RunControls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    .state-control {
        min-width: 260px;
    }
`;

const LoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 0;
`;

const PaginationRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 0;
    border-top: 1px solid #e0e0e0;

    .rows-per-page {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 600;
    }

    .page-controls {
        display: flex;
        align-items: center;
        gap: 12px;
        justify-content: flex-end;
        flex: 1;
        min-width: 260px;
        flex-wrap: wrap;
    }

    .page-summary {
        white-space: nowrap;
    }
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const Section = styled(Box)`
    padding: 16px;
`;

